import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Gift, Copy, CheckCircle2, Loader2, ShieldCheck, AlertOctagon,
  Sparkles, ExternalLink, Send,
} from "lucide-react";

const PROGRAMS = [
  { value: "phlandintel",                label: "PHLandIntel (recommended)" },
  { value: "phpropintel",                label: "PHPropIntel (preview)" },
  { value: "philippines_intel_general",  label: "Philippines Intel: general" },
];

const CHANNELS = [
  ["in_person",            "In person at pickup"],
  ["booking_confirmation", "Booking confirmation email"],
  ["receipt",              "Printed receipt"],
  ["printed_qr",           "Printed QR / flyer"],
  ["messenger",            "Messenger / WhatsApp follow-up"],
  ["email",                "Email"],
  ["website",              "Our website / social"],
  ["other",                "Other"],
];

const STATUS_COPY = {
  requested:    { tone: "bg-blue-100 text-blue-800 border-blue-200",          label: "Request received: waiting for review." },
  under_review: { tone: "bg-amber-100 text-amber-800 border-amber-200",       label: "Under review by the partner team." },
  approved:     { tone: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Approved: your code is being prepared." },
  code_issued:  { tone: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Code issued: ready to share." },
  rejected:     { tone: "bg-rose-100 text-rose-800 border-rose-200",          label: "Request not approved at this time." },
  cancelled:    { tone: "bg-slate-100 text-slate-700 border-slate-200",       label: "Cancelled." },
};


export default function PartnerRewards() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner-rewards/");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!isOwner) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900 max-w-2xl" data-testid="pr-owner-only">
      Only the business owner can request and view partner rewards.
    </div>;
  }
  if (loading || !data) {
    return <div className="py-16 text-center text-slate-500"><Loader2 className="inline w-5 h-5 animate-spin mr-2" />Loading…</div>;
  }

  const active = data.active;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="partner-rewards-page">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium">
          <Gift className="w-3.5 h-3.5" /> Partner Rewards
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold">Earn partner rewards by sharing PHLandIntel</h1>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Many rental customers are exploring places to live, buy land, visit family, or compare areas in the Philippines.
          With a PHLandIntel partner link you can give them a discount and <strong>potentially earn</strong> partner rewards
          when they purchase eligible PHLandIntel products.
        </p>
      </div>

      {active ? (
        active.status === "code_issued"
          ? <IssuedCard req={active} onCancel={null} />
          : <PendingCard req={active} onReload={load} />
      ) : (
        <RequestForm onCreated={load} />
      )}

      {data.items?.length > 1 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pr-history">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Request history</div>
          <ul className="mt-2 divide-y divide-slate-100">
            {data.items.filter((i) => i.id !== active?.id).map((req) => (
              <li key={req.id} className="py-2 text-sm flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-slate-500">{req.id.slice(0, 8)}</span>
                <span>{req.requested_program.replace("_", " ")}</span>
                <StatusPill status={req.status} />
                <span className="text-xs text-slate-400">{(req.created_at || "").slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Disclaimer />
    </div>
  );
}


function RequestForm({ onCreated }) {
  const [form, setForm] = useState({
    requested_program: "phlandintel",
    reason_for_request: "",
    expected_promotion_channels: [],
    notes_from_business: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const toggle = (k) => {
    const next = new Set(form.expected_promotion_channels);
    if (next.has(k)) next.delete(k); else next.add(k);
    setForm({ ...form, expected_promotion_channels: Array.from(next) });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/partner-rewards/request", form);
      if (!data.created) toast(data.message || "An active request already exists.");
      else toast.success("Request sent for review");
      await onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="pr-request-form">
      <div>
        <h2 className="font-display text-xl font-semibold">Request a partner code</h2>
        <p className="text-xs text-slate-500 mt-0.5">Tell us a bit about how you'd share the code.</p>
      </div>

      <label className="block">
        <span className="text-xs font-medium text-slate-700">Program</span>
        <select
          value={form.requested_program}
          onChange={(e) => setForm({ ...form, requested_program: e.target.value })}
          data-testid="pr-form-program"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
        >
          {PROGRAMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="text-xs font-medium text-slate-700">Why do you want a partner code?</span>
        <textarea
          value={form.reason_for_request} rows={3}
          data-testid="pr-form-reason"
          onChange={(e) => setForm({ ...form, reason_for_request: e.target.value })}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
          placeholder="e.g. Many of our renters are visiting from abroad / looking at relocation options."
        />
      </label>

      <div>
        <div className="text-xs font-medium text-slate-700 mb-1">How will you share it?</div>
        <div className="grid sm:grid-cols-2 gap-1.5">
          {CHANNELS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm rounded-lg px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={form.expected_promotion_channels.includes(k)}
                onChange={() => toggle(k)}
                data-testid={`pr-form-channel-${k}`}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs font-medium text-slate-700">Notes (optional)</span>
        <textarea
          value={form.notes_from_business} rows={2}
          data-testid="pr-form-notes"
          onChange={(e) => setForm({ ...form, notes_from_business: e.target.value })}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
        />
      </label>

      <button type="submit" disabled={submitting} data-testid="pr-submit"
              className="px-4 py-2 rounded-lg btn-brand font-medium text-sm inline-flex items-center gap-1 disabled:opacity-60">
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Request partner code
      </button>
    </form>
  );
}

function PendingCard({ req, onReload }) {
  const copy = STATUS_COPY[req.status] || STATUS_COPY.requested;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pr-pending-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${copy.tone}`} data-testid="pr-status-pill">{req.status}</span>
        <span className="text-sm text-slate-700">{copy.label}</span>
      </div>
      <ul className="mt-3 text-sm text-slate-700 space-y-1">
        <li><b>Program:</b> {req.requested_program.replace("_", " ")}</li>
        <li><b>Submitted:</b> {(req.requested_at || req.created_at || "").slice(0, 10)}</li>
      </ul>
      {req.status === "rejected" && req.rejection_reason_public && (
        <p className="mt-3 text-sm text-rose-900 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="pr-rejection-reason">
          {req.rejection_reason_public}
        </p>
      )}
    </div>
  );
}

function IssuedCard({ req }) {
  const copyAndToast = (label, v) => async () => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`, { duration: 1500 }); }
    catch { toast.error("Could not copy"); }
  };
  const customerLine = `Planning to explore land, property, or relocation options in the Philippines? Use our partner code ${req.issued_code} for a PHLandIntel discount: ${req.issued_link}`;
  const shortLine = `Use code ${req.issued_code} for a PHLandIntel discount: ${req.issued_link}`;
  const flyerLine = "Ask us for our PHLandIntel partner code if you are exploring land, property, or relocation in the Philippines.";
  return (
    <div className="space-y-4" data-testid="pr-issued-card">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          <h2 className="font-display text-xl font-semibold text-emerald-900">Your PHLandIntel partner code is ready</h2>
        </div>
        <p className="text-sm text-emerald-900/90 mt-1">
          Use this with renters who are exploring land, property, or relocation options.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <CopyTile label="Partner code" value={req.issued_code} onCopy={copyAndToast("Code", req.issued_code)} testId="pr-copy-code" />
          <CopyTile label="Partner link" value={req.issued_link} onCopy={copyAndToast("Link", req.issued_link)} testId="pr-copy-link" />
        </div>
        {req.discount_percent != null && (
          <p className="mt-3 text-xs text-emerald-900/80 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Customer discount: <b>{req.discount_percent}%</b>{req.commission_terms_summary ? " · " + req.commission_terms_summary : ""}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="pr-share-section">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Suggested sharing copy</div>
        <CopyableTextarea label="Customer message"  value={customerLine} testId="pr-copy-customer-message" />
        <CopyableTextarea label="Short version"     value={shortLine}    testId="pr-copy-short-message" />
        <CopyableTextarea label="Flyer / printed card" value={flyerLine} testId="pr-copy-flyer" />
        <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500" data-testid="pr-qr-placeholder">
          QR code coming soon: print or share the link directly for now.
        </div>
      </div>

      <PerformancePlaceholder />
    </div>
  );
}

function CopyTile({ label, value, onCopy, testId }) {
  return (
    <button type="button" onClick={onCopy} data-testid={testId}
            className="group text-left rounded-lg border border-emerald-200 bg-white px-3 py-2 hover:border-emerald-400 transition">
      <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-semibold">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-sm truncate">{value}</div>
        <Copy className="w-3.5 h-3.5 text-emerald-700" />
      </div>
    </button>
  );
}
function CopyableTextarea({ label, value, testId }) {
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`, { duration: 1500 }); }
    catch { toast.error("Could not copy"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <button type="button" onClick={onCopy} data-testid={testId} className="text-xs underline text-[#1B4332]">
          <Copy className="w-3 h-3 inline mr-0.5" /> Copy
        </button>
      </div>
      <textarea readOnly value={value} rows={2}
                 className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs font-mono outline-none" />
    </div>
  );
}

function PerformancePlaceholder() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pr-performance-placeholder">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Performance</div>
      <div className="mt-2 grid sm:grid-cols-3 gap-3 text-sm">
        <Stat label="Clicks"          value="Not connected yet" />
        <Stat label="Conversions"     value="Not connected yet" />
        <Stat label="Estimated rewards" value="Not connected yet" />
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        Tracking will be added once PHLandIntel webhooks are connected. We never show estimated earnings as a guarantee.
      </p>
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-sm text-slate-700">{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const meta = STATUS_COPY[status] || STATUS_COPY.requested;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${meta.tone}`}>{status}</span>;
}

function Disclaimer() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 leading-relaxed" data-testid="pr-disclaimer">
      <p className="inline-flex items-center gap-1 font-semibold text-slate-700">
        <ShieldCheck className="w-3.5 h-3.5" /> Partner program terms
      </p>
      <p className="mt-1">
        Partner rewards are subject to approval, eligibility, fraud checks, and program terms. Discounts, commissions,
        and payout rules may change. Do not promise customers results or guaranteed discounts unless your code is
        active and shown on this page.
      </p>
      <p className="mt-2">
        ConfirmPH partner rewards are separate from your ConfirmPH subscription. Any rewards shown are not guaranteed
        until approved and paid.
      </p>
    </div>
  );
}
